import React, {
  FC,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import { observer } from "mobx-react";
import Container from "ui/Container/Container";
import Close from "assets/icons/10x10/close.svg";
import PrescriptionIcon from "assets/icons/cheque-white.svg";
import { useStore } from "store";
import { useUrl } from "helpers/hooks";
import { gtm } from "helpers/analytics";
import { cn } from "helpers/style";
import { withTrailSlash } from "helpers/url";
import { LAPTOP, TABLET } from "constants/mixins";
import {
  HEADER_LAPTOP_HEIGHT,
  NAVIGATION_HOVER_TIMEOUT,
} from "constants/variables";
import { ROUTE_ISG, ROUTE_PRESCRIPTION, ROUTE_PROMOS } from "constants/routes";
import NavItem from "./components/NavItem/NavItem";

import styles from "./Catalog.module.scss";

const ProductOfTheDay = dynamic(
  () => import("./components/ProductOfTheDay/ProductOfTheDay"),
  { ssr: false }
);

const calculateHeight = (height, offset) => {
  return `calc(100vh - ${height} - 100% + 10px + ${offset})`;
};

const Catalog: FC = () => {
  const { categories, user, location } = useStore();
  const scrollTop = useRef(null);
  const timer = useRef(null);
  const [showMoreBtn, setShowMoreBtn] = useState(false);
  const [activeIndex, updateActiveIndex] = useState(null);
  const [isCatalogOpened, updateIsCatalogOpened] = useState(false);
  const [isNotCatalogCollapsed, updateIsNotCatalogCollapsed] = useState(false);
  const { getEntityLinkProps, getLinkProps } = useUrl();
  const navSpecialContainerRef = useRef(null);
  const navContainerRef = useRef(null);
  const isMoscow = location.regionID === "moscowregion";
  const prescriptionUrl = user.emias?.url || withTrailSlash(ROUTE_PRESCRIPTION);

  const overlayHeight = useMemo(() => {
    if (!isCatalogOpened) return null;

    const offset = scrollTop.current ? `${scrollTop.current}px` : "0px";
    /* overlay visible only on >laptop, no need another breakpoints height */
    return calculateHeight(HEADER_LAPTOP_HEIGHT, offset);
  }, [isCatalogOpened]);

  const closeCatalog = useCallback(point => {
    if (point === "desktop" && window && window.innerWidth < LAPTOP) return;
    clearTimeout(timer.current);
    updateIsCatalogOpened(false);
    updateActiveIndex(null);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", closeCatalog);

    return () => {
      window.removeEventListener("scroll", closeCatalog);
    };
  }, [closeCatalog]);

  function onOpenMobileCatalog(index, e) {
    if (window && (window.innerWidth >= LAPTOP || window.innerWidth < TABLET))
      return;

    e.preventDefault();

    if (activeIndex === index) {
      updateIsCatalogOpened(false);
      updateActiveIndex(null);
      return;
    }

    scrollTop.current = window.scrollY;
    updateActiveIndex(index);
    updateIsCatalogOpened(true);
  }

  function stopTimer() {
    clearTimeout(timer.current);
  }

  function onOpenDesktopCatalog(index) {
    if (window && window.innerWidth < LAPTOP) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      scrollTop.current = window.scrollY;
      updateIsCatalogOpened(true);
      updateActiveIndex(index);
    }, NAVIGATION_HOVER_TIMEOUT);
  }

  const productOfDay = categories.list[activeIndex]?.productOfDay;

  useEffect(() => {
    if (navContainerRef.current && navSpecialContainerRef.current) {
      if (isNotCatalogCollapsed) {
        setShowMoreBtn(true);
      } else if (
        navContainerRef.current.offsetHeight ===
        navSpecialContainerRef.current.offsetHeight
      ) {
        setShowMoreBtn(false);
      } else {
        setShowMoreBtn(true);
      }
    }
  }, [isNotCatalogCollapsed]);
  return (
    <div
      className={cn([
        styles.catalog,
        isNotCatalogCollapsed && styles.isNotCatalogCollapsed,
      ])}
      onMouseLeave={() => closeCatalog("desktop")}
      onClick={() => closeCatalog("desktop")}
    >
      <Container>
        <div className={styles.catalogContainer}>
          <nav className={styles.catalogNav} ref={navContainerRef}>
            <div
              className={styles.catalogNavSpecial}
              ref={navSpecialContainerRef}
            >
              <Link
                {...getLinkProps(ROUTE_PROMOS, undefined, {
                  withRegion: false,
                  withOrigin: true,
                })}
                passHref
                legacyBehavior
              >
                <NavItem
                  onClick={gtm.clickPromoBtn}
                  title="Акции"
                  type="promo"
                  isCatalogOpened={isCatalogOpened}
                  onMouseOver={closeCatalog}
                  className={styles.catalogNavSpecialItem}
                  employeeMode={user.isEmployeeMode}
                />
              </Link>
              <Link
                {...getEntityLinkProps("discounts")}
                passHref
                legacyBehavior
              >
                <NavItem
                  onClick={gtm.clickDiscountsBtn}
                  title="Скидки"
                  type="discounts"
                  isCatalogOpened={isCatalogOpened}
                  onMouseOver={closeCatalog}
                  className={styles.catalogNavSpecialItem}
                  employeeMode={user.isEmployeeMode}
                />
              </Link>
              <Link {...getLinkProps(ROUTE_ISG)} passHref legacyBehavior>
                <NavItem
                  onClick={gtm.clickISGBtn}
                  title="Уценка"
                  type="isg"
                  isCatalogOpened={isCatalogOpened}
                  onMouseOver={closeCatalog}
                  className={styles.catalogNavSpecialItem}
                  employeeMode={user.isEmployeeMode}
                />
              </Link>
              {categories.list.map((item, index) => (
                <Link
                  key={index}
                  {...getEntityLinkProps("category", item)}
                  passHref
                  legacyBehavior
                >
                  <NavItem
                    title={item.name}
                    active={index === activeIndex}
                    isCatalogOpened={isCatalogOpened}
                    type={item.alias}
                    onClick={e =>
                      !item.children.length
                        ? updateActiveIndex(index)
                        : onOpenMobileCatalog(index, e)
                    }
                    onMouseOver={() => {
                      if (!item.children.length) {
                        updateActiveIndex(index);
                        updateIsCatalogOpened(false);
                      } else {
                        onOpenDesktopCatalog(index);
                      }
                    }}
                    onmouseleave={stopTimer}
                  />
                </Link>
              ))}
            </div>
          </nav>

          {showMoreBtn && (
            <button
              className={styles.catalogMore}
              onClick={() =>
                updateIsNotCatalogCollapsed(!isNotCatalogCollapsed)
              }
            >
              <div className={styles.catalogMoreHamburger}>
                <span />
              </div>
              Ещё
            </button>
          )}
          {(isMoscow || user.emias?.prescriptions?.length > 0) && (
            <Link
              {...getLinkProps(prescriptionUrl, undefined, {
                withRegion: false,
                withOrigin: true,
              })}
              className={styles.catalogEmias}
              passHref
            >
              <PrescriptionIcon />
              Рецепты{" "}
              {user.emias?.prescriptions?.length > 0 && (
                <div className={styles.catalogEmiasDots}>
                  {user.emias.prescriptions.length}
                </div>
              )}
            </Link>
          )}
        </div>
      </Container>

      <div
        className={cn([styles.catalogList, isCatalogOpened && styles.opened])}
      >
        <Close onClick={closeCatalog} />

        <Container>
          <div
            className={cn([styles.list, productOfDay && styles.withProduct])}
          >
            <div>
              {categories.list[activeIndex]?.children.map((item, index) => {
                if (item.isMeta) return null;

                return (
                  <Link
                    key={index}
                    {...getEntityLinkProps("category", item)}
                    className={styles.listItem}
                    onClick={closeCatalog}
                    passHref
                  >
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </div>

          {productOfDay && <ProductOfTheDay product={productOfDay} />}
        </Container>

        {productOfDay && <div className={styles.laptopBackground} />}

        <div
          className={styles.overlay}
          onMouseEnter={closeCatalog}
          style={{ height: overlayHeight }}
        />
      </div>
    </div>
  );
};

export default observer(Catalog);
